import type { SupabaseClient } from "@supabase/supabase-js"

/** One year, in seconds — the Cache-Control max-age Storage serves the object with. */
export const IMMUTABLE_CACHE_SECONDS = "31536000"

export interface UploadOrgImageResult {
  path: string
  publicUrl: string
}

/**
 * NOS-251: media-library originals are served with long-lived cache headers,
 * which is only safe when an uploaded path never changes content. Every
 * upload therefore gets a fresh versioned filename (`<prefix>-<ts>.<ext>`)
 * instead of upserting a stable path — the new public URL propagates through
 * whatever DB column references it, and CDN/browser caches never go stale.
 * (M21 import rehosting gets the same header via content-addressed paths.)
 */
export async function uploadVersionedOrgImage(
  supabase: SupabaseClient,
  bucket: string,
  prefix: string,
  file: File
): Promise<{ data: UploadOrgImageResult | null; error: { message: string } | null }> {
  const ext = file.name.split(".").pop()?.toLowerCase() || "png"
  const path = `${prefix}-${Date.now().toString(36)}.${ext}`
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    contentType: file.type,
    cacheControl: IMMUTABLE_CACHE_SECONDS,
  })
  if (error) return { data: null, error }
  const { data } = supabase.storage.from(bucket).getPublicUrl(path)
  if (!data?.publicUrl) {
    return { data: null, error: { message: "Could not resolve public URL" } }
  }
  return { data: { path, publicUrl: data.publicUrl }, error: null }
}
